import React from "react";
import dynamic from "next/dynamic";
import { ENV_NAME } from "@gcloud-npm/utils.enums.environment";
import calculateHeaderParams from "@gcloud-npm/node.build-process.build-environments";
import Request from "@gcloud-npm/utils.fetch";

export const currentEnv = `${process.env.NEXT_PUBLIC_ENV_NAME}`;

const ENTER_KEY = "enter";
const RIGHT_MOUSE_BUTTON = 2;

const CMS_MODAL_PAGE = "cms-modal-page:";
const CMS_MODAL_CONTENT = "cms-modal:";
const ANCHOR = "anchor:";
const NONE = "enlace:";

export const BEHAVIOUR_KIND = {
  NONE,
  CMS_MODAL_PAGE,
  CMS_MODAL_CONTENT,
  ANCHOR,
};

export const appendHost = (href = "") => {
  let host = "";
  if (href.indexOf("http") === -1 && href.indexOf("//") === -1) {
    switch (currentEnv) {
      case ENV_NAME.DEV:
      case ENV_NAME.LOCAL:
        host = `http://127.0.0.1:8089`;
        break;
      case ENV_NAME.CERT:
      case ENV_NAME.INTE:
        host = `https://www.movistar.es.cert`;
        break;
      case ENV_NAME.CODE:
        host = `https://dev.ssr.col.movistar.es`;
        break;
      case ENV_NAME.QA_SSR:
        host = `https://www.movistar.es/qa`;
        break;
      case ENV_NAME.DEV_SSR:
        host = `https://www.movistar.es/develop`;
        break;
      case ENV_NAME.PREPROD:
        host = `https://www.movistar.es.telefonica`;
        break;
      default:
      case ENV_NAME.PROD:
        host = `https://www.movistar.es`;
        break;
    }
  }

  if (
    href &&
    href.indexOf("#") === -1 &&
    href.indexOf("?") === -1 &&
    href.indexOf(".") === -1 &&
    href[href.length - 1] !== "/"
  ) {
    href = `${href}/`;
  }
  return `${host}${href}`;
};

export const getReadyLinkForDev = (href) => {
  let devLink = href;
  const projectBasePath = `${process.env.NEXT_PUBLIC_PROJECT_BASE_PATH}`;
  // eliminamos el basename del proyecto
  if (
    projectBasePath !== "/" &&
    href.indexOf("http") === -1 &&
    href.indexOf("//") === -1
  ) {
    devLink = href.replace(`${process.env.NEXT_PUBLIC_PROJECT_BASE_PATH}`, "");
  }

  if (!devLink) {
    devLink = "/";
  }

  if (
    devLink &&
    devLink.indexOf("#") === -1 &&
    devLink.indexOf("?") === -1 &&
    devLink.indexOf(".") === -1 &&
    devLink[devLink.length - 1] !== "/"
  ) {
    devLink = `${devLink}/`;
  }

  return devLink;
};

export const checkIfHasHrefOperators = (href) => {
  let result = false;
  const OPERATORS = ["tel:", "mailto:"];
  OPERATORS.forEach((item) => {
    if (!result && href && href.indexOf(item) > -1) {
      result = true;
    }
  });
  return result;
};

const properKeyChecker = (e) =>
  e && e.code && e.code.toLowerCase() === ENTER_KEY;

const properMouseButtonChecker = (e) =>
  (e && e.which && e.which !== RIGHT_MOUSE_BUTTON) ||
  (e && e.button && e.button !== RIGHT_MOUSE_BUTTON);

const wrapperFunction = async ({ action2Execute = null, taggingInfo }) => {
  try {
    const isFrontApp = typeof window !== "undefined";
    if (isFrontApp) {
      try {
        let moduleSendGaEvent =
          await import("@gcloud-npm/utils.metrics.send-ga-component-event");
        moduleSendGaEvent = moduleSendGaEvent.default;
        moduleSendGaEvent(taggingInfo);
      } catch (e) {
        /* lo unico que puede haber es fallo de conectividad y no es bloqueante */
      }
    }
    if (action2Execute) {
      action2Execute();
    }
  } catch (error) {
    let info = "";
    try {
      info = `Element taggingInfo: ${JSON.stringify(taggingInfo)}`;
    } catch (e) {
      console.error("No se pudo generar más info en base al taggingInfo");
    }
    // de abajo
    let moduleFrontSonda =
      await import("@gcloud-npm/utils.monitoring.front-sonda");
    moduleFrontSonda = moduleFrontSonda.default;
    const sonda = new moduleFrontSonda({
      processName: `Custom-link-${process.env.NEXT_PUBLIC_PROJECT_ID}`,
      filePath: `Custom-link-${window.location.href}`,
    });
    sonda.errorMessage(error);
    console.error({ action2Execute });
    throw error;
  }
};

export const anchorFunction = ({ href }) => {
  try {
    const anchor = href.split(":")[1];
    const element = document.querySelector(anchor);
    if (element && element.scrollIntoView) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
      element.focus();
    }
  } catch (e) {
    console.error(e);
  }
};

export const generateOnHandlerMethods = ({
  onClick,
  taggingInfo,
  buttonTypeHtml,
  disabled,
  isCmsModalPageHref,
  isCmsModalContentHref,
  isAnchor,
  alias,
  context,
  addModal,
  href,
  additionalContent,
  additionalContentHeader,
  isAstroHref,
  lang = ""
}) => {
  let newOnClick = null;
  let newOnKeyDown = null;
  let newOnMouseDown = null;
  let action2Execute = () => {};

  /* ---------------------------- PARTE MODALFICHA ---------------------------- */
  // isCmsModalPageHref && onClick
  if (isCmsModalPageHref && onClick) {
    action2Execute = () => {
      cmsPageModalHandler({ href, addModal, isAstroHref });
      onClick();
    };
  }
  // isCmsModalPageHref && !onClick
  if (isCmsModalPageHref && !onClick) {
    action2Execute = () => {
      cmsPageModalHandler({
        href,
        addModal,
        additionalContent,
        additionalContentHeader,
        isAstroHref,
      });
    };
  }
  /* ----------------------------- PARTE MODAL CMD ---------------------------- */
  if (isCmsModalContentHref && onClick) {
    action2Execute = () => {
      cmsModalContentHandler({ alias, addModal, context, href, lang });
      onClick();
    };
  }
  if (isCmsModalContentHref && !onClick) {
    action2Execute = () => {
      cmsModalContentHandler({ alias, addModal, context, href, lang });
    };
  }
  /* ------------------------------ PARTE ANCHOR ------------------------------ */
  if (isAnchor) {
    action2Execute = () => {
      anchorFunction({ href });
    };
  }

  /* ---------------------------- onclick sin extra --------------------------- */
  if (onClick && !isCmsModalContentHref && !isCmsModalPageHref) {
    action2Execute = () => {
      onClick();
    };
  }

  if (onClick && buttonTypeHtml === "submit") {
    action2Execute = null;
  }

  newOnClick = (e) => {
    taggingInfo = {
      ...taggingInfo,
      eventType: "click",
    };
    wrapperFunction({ taggingInfo, action2Execute });
  };

  if (!href) {
    newOnKeyDown = (e) => {
      if (properKeyChecker(e)) {
        e.preventDefault();
        //Evita ejecuciones duplicadas del evento enter
        taggingInfo = {
          ...taggingInfo,
          eventType: "enter",
        };
        wrapperFunction({ taggingInfo, action2Execute });
      }
    };
  }
  newOnMouseDown = (e) => {
    if (properMouseButtonChecker(e)) {
      taggingInfo = {
        ...taggingInfo,
        eventType: "clickMouseWheel",
      };
      wrapperFunction({ taggingInfo, action2Execute });
    }
  };

  if (disabled) {
    newOnClick = () => {
      return false;
    };
    newOnKeyDown = () => {
      return false;
    };
    newOnMouseDown = () => {
      return false;
    };
  }
  return {
    newOnClick,
    newOnKeyDown,
    newOnMouseDown,
  };
};

export const extendsAccesibility = ({
  cmpTitle,
  cmpAriaLabel,
  cmpScreenRedable,
  hasChildren,
  taggingInfo,
}) => {
  let title = cmpTitle;
  let ariaLabel = cmpAriaLabel || taggingInfo?.componentContent;
  const ariaHidden = !cmpScreenRedable;

  return {
    title,
    ariaLabel,
    ariaHidden,
  };
};

export const cmsPageModalHandler = async ({
  href,
  addModal,
  additionalContent,
  additionalContentHeader,
  isAstroHref,
}) => {
  try {
    let url = href.split(":")[1];
    const id = `cms-modal-page${url.replace(/\//g, "--")}`;
    let width = null;
    try {
      const getUrlParams =
        await import("@gcloud-npm/utils.common.get-url-params");
      const urlParams = getUrlParams.default({
        groupByObject: false,
        allowedParams: false,
        excludeHash: true,
        currentUrl: new URL(url, window.location.origin),
      });

      width = urlParams.width;
    } catch (e) {}
    if (url && window.top.location === window.self.location) {
      let device = "MOBILE";
      try {
        let getDeviceKindDependsOnViewPortModule =
          await import("@gcloud-npm/utils.common.get-device-kind-depends-on-viewport");
        getDeviceKindDependsOnViewPortModule =
          getDeviceKindDependsOnViewPortModule.default;
        device = getDeviceKindDependsOnViewPortModule();
      } catch (e) {
        /* Solo es posible un fallo de conectividad */
      }
      const ModalWeb = dynamic(
        () => import("@/bit/components/components.cms-block.cms-modal-page"),
      );

      // Si es una url de astro la normalizo
      if (process.env.NEXT_PUBLIC_ASTRO_MAKER && isAstroHref) {
        const [path, query] = href.split("?"); // separa ruta y parámetros
        href = `/astro-maker/page/edit/?page=${path}${query ? "&" + query : ""}&cms=false&webview=true`;
      }
      addModal({
        id,
        Element2Show: ModalWeb,
        modalProps: {
          closeIdByClickingOutside: "",
          opacityLayoutColor: "blur",
          closeByClickingOutside: true,
          contentPosition:
            device === "MOBILE" ? "center--bottom" : "center--center",
          inSceneEffect:
            device === "MOBILE" || device === "TABLET"
              ? "fadeInBottom"
              : "fadeInRight",
          outSceneEffect:
            device === "MOBILE" || device === "TABLET"
              ? "fadeOutBottom"
              : "fadeOutRight",
          scrollAllowed: false,
          createAnchor: false,
        },
        elementProps: {
          url,
          additionalContent,
          additionalContentHeader,
          width,
        },
      });
    } else if (url && window.top.location !== window.self.location) {
      const message = {
        url,
        action: "redirect",
      };
      window.parent.postMessage(message, "*");
    }
  } catch (e) {
    console.error(e);
  }
};

const getAstroPageProps = ({ alias, cms, context, lang }) => {
  let host = "https://ssr.col.movistar.es";

  if (process.env.NEXT_PUBLIC_ASTRO_MAKER) {
    if (currentEnv === "local") {
      host = `http://127.0.0.1:8089`;
    } else {
      host = `https://dev.ssr.col.movistar.es`;
    }
  } else if (currentEnv === "qa_ssr") {
      host = `https://dev.ssr.col.movistar.es`;
  }
  let URL_API = `${host}/api/astro-maker/v1/get-page-content/?alias=${alias}&cms=${cms}`;
  if (lang) {
    URL_API = URL_API + `&lang=${lang}`
  }
  const headers = calculateHeaderParams(false, "ssr-backend", context);
  const request = new Request({
    method: "GET",
    endpoint: URL_API,
    headers: {
      ...headers,
    },
  });

  return request.makeRequest();
};

const isEditorEnv = () => {
  return (
    `${process.env.NEXT_PUBLIC_ENV_NAME}` === "local" ||
    `${process.env.NEXT_PUBLIC_ENV_NAME}` === "code" ||
    `${process.env.NEXT_PUBLIC_ENV_NAME}` === "qa_ssr"
  );
};

const MODAL_PROPS = {
  closeIdByClickingOutside: "",
  contentPosition: "right--top",
  opacityLayoutColor: "blur",
  inSceneEffect: "fadeIn",
  contentInsideContainer: false,
  outSceneEffect: "fadeOut",
  closeByClickingOutside: true,
  scrollAllowed: false,
  createAnchor: false,
};

export const cmsModalContentHandler = async ({
  alias: aliasProp,
  addModal,
  context,
  href,
  lang
}) => {
  let pathname = "";
  let modalContent = null;
  let ModalComponent = null;

  let modalResponse = null;
  let CmsBlock = null;
  let modalId = "";

  try {
    let alias = ";";
    let modalProps = MODAL_PROPS;

    if (href && href.indexOf(":") > 0) {
      const tokensHref = href.split(":");
      if (tokensHref && tokensHref[1]) {
        alias = tokensHref[1];
      }

      if (tokensHref && tokensHref[2]) {
        modalId = tokensHref[2];
      }
    }

    // De aqui saco las props de posicion y transicion
    try {
      modalContent = await getAstroPageProps({
        alias,
        cms: currentEnv !== "prod",
        context,
        lang
      });
      pathname = modalContent?.pathname;
    } catch (error) {}

    if (modalContent) {
      ModalComponent = dynamic(
        () => import("@/bit/components/components.cms-block.cms-modal"),
      );

      // añadimos funcion de cierre de modal y modal props si tenemos
      if (modalContent) {
        modalContent = {
          ...modalContent,
          pathname,
          modalId: modalContent.modalId || "",
          modalTitle: modalContent.modalTitle || "",
          onClose: () => {
            addModal({ id: "" });
          },
        };

        if (modalContent["cms-modal-props"]) {
          modalProps = modalContent["cms-modal-props"];
        }
      }

      if (pathname && modalContent) {
        addModal({
          id: modalContent.modalId || alias,
          Element2Show: ModalComponent,
          modalProps,
          elementProps: modalContent,
        });
      }
    } else {
      // SI NO ENCONTRAMOS CONTENIDO DE MODAL VOY A ASUMIR QUE NO ESTAMOS EN ASTRO PARA RETROCOMPATIBILIDAD
      const modalAlias = `${aliasProp}-modales`;
      ModalComponent = dynamic(
        () => import("@/bit/components/components.cms-block.cms-modal-old"),
      );
      CmsBlock = dynamic(() => import("@/bit/components/components.cms/cms-block"));
      let cmsPromiseModule =
        await import("@gcloud-npm/adabyron.page.get-props");
      cmsPromiseModule = cmsPromiseModule.default;
      modalResponse = await cmsPromiseModule({
        orionCmsAlias: alias,
        context,
        preRenderEnv: `${
          process.env.NEXT_PUBLIC_ENV_NAME !== "prod" ? "qa_ssr" : "prod"
        }`,
        headerLocation: "",
        modalId,
        cms: isEditorEnv(),
        build: false,
      });

      modalResponse = modalResponse?.orion;

      // si viene de la contribución sabemos que pestaña abrir, sino el back nos devolverá la primera
      const finalModalId = modalId || modalResponse.modalId;

      // Vemos si tienemos indicadas propiedades específicas
      if (
        modalResponse &&
        modalResponse[finalModalId] &&
        modalResponse[finalModalId]["cms-modal-props"]
      ) {
        // Ya no borramos el cms de modal props para poder contribuirlo
        modalProps = modalResponse[finalModalId]["cms-modal-props"];
      }

      // añadimos funcion de cierre de modal
      if (modalResponse[finalModalId]) {
        modalResponse[finalModalId] = {
          ...modalResponse[finalModalId],
          modalAlias,
          modalId,
          onClose: () => {
            addModal({ id: "" });
          },
        };
      }

      addModal({
        id: finalModalId,
        Element2Show: () => (
          <CmsBlock
            alias={alias}
            modalId={finalModalId}
            orionInfo={modalResponse}
            blockName={finalModalId}
            Component={ModalComponent}
          />
        ),
        modalProps,
      });
    }
  } catch (e) {
    console.error(e);
  }
};
